import React, { useState } from 'react';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import base from '../Services/BaseService';

const formatCurrency = (value) => Number(value || 0).toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP'
});
const hasValidPrecision = (value) => /^-?(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(value.trim());

const BudgetBalanceModal = ({ budget, closeModal, onSaved }) => {
    const [action, setAction] = useState('add');
    const [amount, setAmount] = useState('');
    const [confirmReset, setConfirmReset] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submitAdjustment = async (adjustmentAction = action) => {
        const numericAmount = Number(amount);
        if (adjustmentAction !== 'reset' && (!Number.isFinite(numericAmount) || !hasValidPrecision(amount) || (adjustmentAction !== 'set' && numericAmount <= 0))) {
            setError(adjustmentAction === 'set'
                ? 'Enter a valid amount with no more than two decimal places.'
                : 'Enter an amount greater than zero with no more than two decimal places.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            await base.update(`Dashboard/budgetbalance/${encodeURIComponent(budget.categoryId)}`, {
                action: adjustmentAction,
                amount: adjustmentAction === 'reset' ? null : numericAmount
            });
            onSaved();
            closeModal();
        } catch (requestError) {
            setError('The balance could not be updated. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen toggle={closeModal} backdrop="static">
            <ModalHeader className="bg-modal" toggle={closeModal}>
                <div className="page-header">Manage Budget Balance</div>
            </ModalHeader>
            <ModalBody>
                <p><strong>{budget.categoryId} — {budget.categoryDescription}</strong></p>
                <p>Current balance: <strong>{formatCurrency(budget.totalRemaining)}</strong></p>

                {confirmReset ? (
                    <div className="alert alert-warning" role="alert">
                        <p className="mb-2">Reset this budget balance from <strong>{formatCurrency(budget.totalRemaining)}</strong> to <strong>{formatCurrency(0)}</strong>?</p>
                        <p className="mb-0">This change cannot be undone.</p>
                    </div>
                ) : (
                    <FormGroup>
                        <Label for="balanceAction">Adjustment</Label>
                        <Input id="balanceAction" type="select" value={action} onChange={(event) => { setAction(event.target.value); setError(''); }}>
                            <option value="add">Add to balance</option>
                            <option value="subtract">Subtract from balance</option>
                            <option value="set">Set balance to</option>
                        </Input>
                        <Label for="balanceAmount" className="mt-3">Amount (£)</Label>
                        <Input
                            id="balanceAmount"
                            type="number"
                            step="0.01"
                            min={action === 'set' ? undefined : '0.01'}
                            value={amount}
                            onChange={(event) => { setAmount(event.target.value); setError(''); }}
                            placeholder="0.00"
                        />
                    </FormGroup>
                )}

                {error && <div className="text-danger" role="alert">{error}</div>}
            </ModalBody>
            <ModalFooter>
                {confirmReset ? (
                    <>
                        <Button color="secondary" size="sm" className="btn-text b-width" onClick={() => setConfirmReset(false)} disabled={saving}>Back</Button>
                        <Button color="danger" size="sm" className="btn-text b-width" onClick={() => submitAdjustment('reset')} disabled={saving}>
                            {saving ? 'Resetting…' : 'Confirm reset'}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button color="danger" size="sm" className="btn-text b-width" onClick={() => { setError(''); setConfirmReset(true); }} disabled={saving}>Reset to £0</Button>
                        <Button color="secondary" size="sm" className="btn-text b-width" onClick={closeModal} disabled={saving}>Cancel</Button>
                        <Button color="success" size="sm" className="btn-text b-width" onClick={() => submitAdjustment()} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default BudgetBalanceModal;
